/**
 * CatalogSlider -- horizontally scrollable product browser strip.
 *
 * Composed of two layers:
 *   1. Product strip -- scroll-snapping thumbnail cards.
 *   2. Variant strip -- color-dot row for the active product's variants.
 *
 * Arrow buttons are rendered but auto-hidden when there is no overflow.
 * Keyboard navigation (ArrowLeft/ArrowRight) moves product selection.
 * Thumbnails are lazy-loaded via IntersectionObserver.
 *
 * CSS class prefix is configurable via `cssPrefix` (default: 'mb-'). This
 * allows each widget to use its own namespace while sharing structural code.
 */

import type { Product, Variant } from '../data/provider.js';

export interface CatalogSliderOptions {
  container: HTMLElement;
  onProductSelect?: (product: Product) => void;
  onVariantSelect?: (variant: Variant) => void;
  /** CSS class prefix. Defaults to 'mb-'. */
  cssPrefix?: string;
}

export class CatalogSlider {
  private readonly container: HTMLElement;
  private readonly onProductSelect?: (product: Product) => void;
  private readonly onVariantSelect?: (variant: Variant) => void;
  private readonly p: string; // CSS prefix shorthand

  private products: Product[] = [];
  private selectedProductId: string | null = null;
  private selectedVariantId: string | null = null;

  // Root elements -- built once in _build(), replaced on setProducts()
  private root: HTMLDivElement | null = null;
  private productList: HTMLDivElement | null = null;
  private variantStrip: HTMLDivElement | null = null;
  private prevBtn: HTMLButtonElement | null = null;
  private nextBtn: HTMLButtonElement | null = null;

  // IntersectionObserver for lazy image loading
  private imgObserver: IntersectionObserver | null = null;

  // ResizeObserver to show/hide arrow buttons on overflow changes
  private resizeObserver: ResizeObserver | null = null;

  // Bound event handlers (kept for removal in destroy)
  private readonly _onKeydown: (e: KeyboardEvent) => void;

  constructor(options: CatalogSliderOptions) {
    this.container = options.container;
    this.p = options.cssPrefix ?? 'mb-';
    if (options.onProductSelect !== undefined) {
      this.onProductSelect = options.onProductSelect;
    }
    if (options.onVariantSelect !== undefined) {
      this.onVariantSelect = options.onVariantSelect;
    }

    this._onKeydown = this._handleKeydown.bind(this);

    this._build();
  }

  // -- Public API -------------------------------------------------------------

  /**
   * Replace the product list and re-render the strip.
   * Preserves the selected product/variant when the same IDs exist in the new
   * list; otherwise falls back to the first product.
   */
  setProducts(products: Product[]): void {
    this.products = products;

    // Preserve selection when possible
    const stillSelected =
      this.selectedProductId !== null &&
      products.some((p) => p.id === this.selectedProductId);

    if (!stillSelected) {
      this.selectedProductId = products.length > 0 ? (products[0]?.id ?? null) : null;
      this.selectedVariantId = null;
    }

    this._renderProductStrip();
    this._renderVariantStrip();
    this._updateArrows();
  }

  /**
   * Programmatically highlight a product card and update the variant strip.
   * Does NOT fire the onProductSelect callback -- callers should invoke it
   * themselves when needed (avoids re-entrant loops).
   */
  selectProduct(productId: string): void {
    if (this.selectedProductId === productId) return;
    this.selectedProductId = productId;
    this.selectedVariantId = null;
    this._syncProductHighlight();
    this._renderVariantStrip();
    this._scrollProductIntoView(productId);
  }

  /**
   * Programmatically highlight a variant dot.
   * Does NOT fire the onVariantSelect callback.
   */
  selectVariant(variantId: string): void {
    if (this.selectedVariantId === variantId) return;
    this.selectedVariantId = variantId;
    this._syncVariantHighlight();
  }

  /**
   * Remove all DOM nodes and event listeners.
   * Call when the modal closes.
   */
  destroy(): void {
    this.imgObserver?.disconnect();
    this.imgObserver = null;

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.container.removeEventListener('keydown', this._onKeydown);

    if (this.root?.parentNode) {
      this.root.parentNode.removeChild(this.root);
    }
    this.root = null;
    this.productList = null;
    this.variantStrip = null;
    this.prevBtn = null;
    this.nextBtn = null;
  }

  // -- Private: DOM construction ----------------------------------------------

  private _build(): void {
    const p = this.p;

    // Outer wrapper
    const root = document.createElement('div');
    root.className = `${p}catalog`;
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', 'Product catalog');

    // Arrow -- previous
    const prevBtn = document.createElement('button');
    prevBtn.className = `${p}catalog__arrow ${p}catalog__arrow--prev`;
    prevBtn.type = 'button';
    prevBtn.setAttribute('aria-label', 'Scroll to previous products');
    prevBtn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none"
        stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
        stroke-linejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6"/>
    </svg>`;
    prevBtn.addEventListener('click', () => this._scrollBy(-1));

    // Scrolling product list
    const productList = document.createElement('div');
    productList.className = `${p}product-strip`;
    productList.setAttribute('role', 'listbox');
    productList.setAttribute('aria-label', 'Products');

    // Arrow -- next
    const nextBtn = document.createElement('button');
    nextBtn.className = `${p}catalog__arrow ${p}catalog__arrow--next`;
    nextBtn.type = 'button';
    nextBtn.setAttribute('aria-label', 'Scroll to next products');
    nextBtn.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none"
        stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
        stroke-linejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6"/>
    </svg>`;
    nextBtn.addEventListener('click', () => this._scrollBy(1));

    // Variant dots strip -- lives below the product cards
    const variantStrip = document.createElement('div');
    variantStrip.className = `${p}variant-strip`;
    variantStrip.setAttribute('role', 'listbox');
    variantStrip.setAttribute('aria-label', 'Color variants');

    // Product row wrapper (arrows + list)
    const productRow = document.createElement('div');
    productRow.className = `${p}product-row`;
    productRow.appendChild(prevBtn);
    productRow.appendChild(productList);
    productRow.appendChild(nextBtn);

    root.appendChild(productRow);
    root.appendChild(variantStrip);
    this.container.appendChild(root);

    this.root = root;
    this.productList = productList;
    this.variantStrip = variantStrip;
    this.prevBtn = prevBtn;
    this.nextBtn = nextBtn;

    // Keyboard navigation on the container
    this.container.addEventListener('keydown', this._onKeydown);

    // Lazy image loading
    if (typeof IntersectionObserver !== 'undefined') {
      this.imgObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              const src = img.dataset['src'];
              if (src) {
                img.src = src;
                delete img.dataset['src'];
                this.imgObserver?.unobserve(img);
              }
            }
          }
        },
        { root: productList, rootMargin: '0px 100px 0px 100px' },
      );
    }

    // Watch for overflow changes to show/hide arrows
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this._updateArrows());
      this.resizeObserver.observe(productList);
    }
  }

  // -- Private: rendering -----------------------------------------------------

  private _renderProductStrip(): void {
    const list = this.productList;
    if (!list) return;

    list.innerHTML = '';

    for (const product of this.products) {
      const card = this._makeProductCard(product);
      list.appendChild(card);

      // Observe thumbnail for lazy load
      const img = card.querySelector('img');
      if (img && this.imgObserver) {
        this.imgObserver.observe(img);
      }
    }
  }

  private _makeProductCard(product: Product): HTMLDivElement {
    const p = this.p;
    const card = document.createElement('div');
    card.className = `${p}product-card`;
    card.dataset['productId'] = product.id;
    card.setAttribute('role', 'option');
    card.setAttribute('aria-selected', String(product.id === this.selectedProductId));
    card.setAttribute('tabindex', product.id === this.selectedProductId ? '0' : '-1');
    card.title = product.name;

    if (product.id === this.selectedProductId) {
      card.classList.add(`${p}product-card--selected`);
    }

    // Thumbnail
    const img = document.createElement('img');
    img.className = `${p}product-card__thumb`;
    img.alt = product.name;

    if (this.imgObserver) {
      // Deferred load via observer
      img.dataset['src'] = product.thumbnailUrl;
      img.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // 1x1 transparent GIF
    } else {
      img.src = product.thumbnailUrl;
    }

    // Name label
    const name = document.createElement('span');
    name.className = `${p}product-card__name`;
    name.textContent = product.name;

    // Footer row: name + inline variant dots
    const footer = document.createElement('div');
    footer.className = `${p}product-card__footer`;

    const tryOnLabel = document.createElement('span');
    tryOnLabel.className = `${p}product-card__tryon-label`;
    tryOnLabel.textContent = 'Try On';

    footer.appendChild(name);
    footer.appendChild(tryOnLabel);

    card.appendChild(img);
    card.appendChild(footer);

    // Inline variant dots -- rendered directly under the product name
    if ((product.variants ?? []).length > 0) {
      const inlineDots = document.createElement('div');
      inlineDots.className = `${p}product-card__variants`;
      for (const variant of product.variants) {
        const dot = document.createElement('button');
        dot.className = `${p}variant-dot ${p}variant-dot--inline`;
        dot.type = 'button';
        dot.dataset['variantId'] = variant.id;
        dot.setAttribute('aria-label', variant.name);
        dot.title = variant.name;

        if (variant.color) {
          dot.style.backgroundColor = variant.color;
        } else if (variant.textureUrl) {
          dot.style.backgroundImage = `url(${variant.textureUrl})`;
          dot.style.backgroundSize = 'cover';
        } else {
          dot.textContent = variant.name.slice(0, 2).toUpperCase();
          dot.classList.add(`${p}variant-dot--text`);
        }

        if (variant.id === this.selectedVariantId) {
          dot.classList.add(`${p}variant-dot--selected`);
        }

        dot.addEventListener('click', (e) => {
          e.stopPropagation(); // don't trigger card click
          this._activateVariant(variant);
        });

        inlineDots.appendChild(dot);
      }
      card.appendChild(inlineDots);
    }

    // Click
    card.addEventListener('click', () => {
      this._activateProduct(product);
    });

    // Keyboard -- Enter / Space activate
    card.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this._activateProduct(product);
      }
    });

    return card;
  }

  private _renderVariantStrip(): void {
    const strip = this.variantStrip;
    if (!strip) return;

    strip.innerHTML = '';

    const product = this.products.find((p) => p.id === this.selectedProductId);
    if (!product || (product.variants ?? []).length === 0) {
      strip.style.display = 'none';
      return;
    }

    strip.style.display = '';

    for (const variant of product.variants) {
      const dot = this._makeVariantDot(variant);
      strip.appendChild(dot);
    }
  }

  private _makeVariantDot(variant: Variant): HTMLButtonElement {
    const p = this.p;
    const dot = document.createElement('button');
    dot.className = `${p}variant-dot`;
    dot.type = 'button';
    dot.dataset['variantId'] = variant.id;
    dot.setAttribute('aria-label', variant.name);
    dot.setAttribute('aria-selected', String(variant.id === this.selectedVariantId));
    dot.setAttribute('role', 'option');

    if (variant.color) {
      dot.style.backgroundColor = variant.color;
    } else if (variant.textureUrl) {
      dot.style.backgroundImage = `url(${variant.textureUrl})`;
      dot.style.backgroundSize = 'cover';
    } else {
      // Fallback: show first two letters of the variant name
      dot.textContent = variant.name.slice(0, 2).toUpperCase();
      dot.classList.add(`${p}variant-dot--text`);
    }

    if (variant.id === this.selectedVariantId) {
      dot.classList.add(`${p}variant-dot--selected`);
    }

    dot.title = variant.name;

    dot.addEventListener('click', () => {
      this._activateVariant(variant);
    });

    return dot;
  }

  // -- Private: state transitions ---------------------------------------------

  private _activateProduct(product: Product): void {
    const wasSelected = product.id === this.selectedProductId;
    this.selectedProductId = product.id;
    this.selectedVariantId = null;

    this._syncProductHighlight();
    this._renderVariantStrip();

    if (!wasSelected) {
      this.onProductSelect?.(product);
    }
  }

  private _activateVariant(variant: Variant): void {
    this.selectedVariantId = variant.id;
    this._syncVariantHighlight();
    this.onVariantSelect?.(variant);
  }

  /** Update aria/class on existing product cards without full re-render. */
  private _syncProductHighlight(): void {
    if (!this.productList) return;
    const p = this.p;

    for (const card of Array.from(this.productList.children) as HTMLElement[]) {
      const isSelected = card.dataset['productId'] === this.selectedProductId;
      card.classList.toggle(`${p}product-card--selected`, isSelected);
      card.setAttribute('aria-selected', String(isSelected));
      card.setAttribute('tabindex', isSelected ? '0' : '-1');
    }
  }

  /** Update aria/class on existing variant dots without full re-render. */
  private _syncVariantHighlight(): void {
    const p = this.p;

    // Update the separate variant strip
    if (this.variantStrip) {
      for (const dot of Array.from(this.variantStrip.children) as HTMLElement[]) {
        const isSelected = dot.dataset['variantId'] === this.selectedVariantId;
        dot.classList.toggle(`${p}variant-dot--selected`, isSelected);
        dot.setAttribute('aria-selected', String(isSelected));
      }
    }

    // Also update inline dots inside product cards
    if (this.productList) {
      const allInlineDots = this.productList.querySelectorAll<HTMLElement>(`.${p}variant-dot--inline`);
      for (const dot of allInlineDots) {
        const isSelected = dot.dataset['variantId'] === this.selectedVariantId;
        dot.classList.toggle(`${p}variant-dot--selected`, isSelected);
      }
    }
  }

  // -- Private: scrolling -----------------------------------------------------

  /**
   * Scroll the product list by one "card width" in the given direction.
   * direction: -1 = left, +1 = right.
   */
  private _scrollBy(direction: -1 | 1): void {
    const list = this.productList;
    if (!list) return;

    // Use the width of the first card as the scroll unit
    const firstCard = list.firstElementChild as HTMLElement | null;
    const cardWidth = firstCard ? firstCard.offsetWidth + 10 /* gap */ : 74;

    if (typeof list.scrollBy === 'function') {
      list.scrollBy({ left: direction * cardWidth * 2, behavior: 'smooth' });
    }
  }

  /** Scroll the card with the given productId into view within the strip. */
  private _scrollProductIntoView(productId: string): void {
    const card = this._cardForProduct(productId);
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    }
  }

  /** Find the card element for a given productId by scanning children. */
  private _cardForProduct(productId: string): HTMLElement | null {
    if (!this.productList) return null;
    for (const child of Array.from(this.productList.children) as HTMLElement[]) {
      if (child.dataset['productId'] === productId) return child;
    }
    return null;
  }

  /** Show/hide prev/next arrows based on whether the list overflows. */
  private _updateArrows(): void {
    const list = this.productList;
    if (!list || !this.prevBtn || !this.nextBtn) return;

    const hasOverflow = list.scrollWidth > list.clientWidth + 2; // +2 for sub-pixel rounding
    this.prevBtn.style.display = hasOverflow ? '' : 'none';
    this.nextBtn.style.display = hasOverflow ? '' : 'none';
  }

  // -- Private: keyboard navigation -------------------------------------------

  private _handleKeydown(e: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    if (this.products.length === 0) return;

    const currentIndex = this.products.findIndex((p) => p.id === this.selectedProductId);
    let nextIndex = currentIndex;

    if (e.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : this.products.length - 1;
    } else {
      nextIndex = currentIndex < this.products.length - 1 ? currentIndex + 1 : 0;
    }

    const nextProduct = this.products[nextIndex];
    if (!nextProduct || nextIndex === currentIndex) return;

    e.preventDefault();
    this._activateProduct(nextProduct);
    this._scrollProductIntoView(nextProduct.id);

    // Move focus to the newly selected card
    this._cardForProduct(nextProduct.id)?.focus();
  }
}
